/**
 * Normalized event DTO shared by all scrapers and API ingestors (PIP-8).
 *
 * This is the canonical intermediate shape produced by every `VenueIngestor`
 * (see PIP-2). Downstream services (backend-api, web, recommendations)
 * consume the `Event` / `Location` / `Artist` database records that the
 * ingestion pipeline writes from this DTO — callers that only need to describe
 * or transport a normalized event should import `NormalizedEvent` from here.
 *
 * Runtime validation lives in `@loci/validation` (`normalizedEventSchema`).
 * The Zod schema is structurally identical to this interface and re-uses the
 * `EVENT_CATEGORIES` / `PLATFORM_EVENT_CATEGORIES` / `EVENT_STATUSES` tuples
 * defined below, so the types cannot drift.
 *
 * Two category fields exist because the scraper-tier taxonomy (what a venue
 * calls the event) is broader than the platform taxonomy (what LOCI
 * surfaces to users). Ingestors record the raw label and map it to the
 * platform slug so DB inserts can resolve `platformCategory` → `Category.id`
 * in the seeded hierarchy without losing the source distinction.
 */

import type { ImageUrls } from '../event';
import { CANONICAL_CATEGORY_SLUGS } from './category-taxonomy';

export const EVENT_CATEGORIES = [
  'concert',
  'sports',
  'theater',
  'comedy',
  'family',
  'festival',
  'other',
] as const;
export type NormalizedEventCategory = (typeof EVENT_CATEGORIES)[number];

/**
 * LOCI platform category slugs (COR-37 closed ten). Each value corresponds to
 * a top-level `Category` row seeded by `packages/db-core/prisma/seed-categories.ts`
 * — `platformCategory` resolves to `Category.slug` at insert time, so M2–M4
 * ingestors must map scraper-tier labels to one of these ten values.
 *
 * Canonical definition lives in `category-taxonomy.ts`; re-exported here so
 * existing `@loci/types` consumers of `PLATFORM_EVENT_CATEGORIES` keep working.
 */
export const PLATFORM_EVENT_CATEGORIES = CANONICAL_CATEGORY_SLUGS;
export type PlatformEventCategory = (typeof PLATFORM_EVENT_CATEGORIES)[number];

export const EVENT_STATUSES = [
  'on_sale',
  'presale',
  'sold_out',
  'postponed',
  'cancelled',
  'announced',
  'completed',
  'archived',
] as const;
export type NormalizedEventStatus = (typeof EVENT_STATUSES)[number];

export interface NormalizedEvent {
  /** UUID v4 assigned by the ingestor for this observation (not the DB id). */
  eventId: string;
  /** Stable identifier from the upstream source (API id, URL slug, etc.). */
  sourceEventId: string;
  /** Internal venue identifier (e.g., slug like `blumenthal-belk-theater`). */
  venueId: string;
  /** Human-readable venue name as displayed to users. */
  venueName: string;
  /**
   * Google Places `place_id` for the venue, when the source (or an ingest-time
   * lookup) resolved one. Optional — most sources don't supply it. PIP-164:
   * when present, the persister matches an existing Location by place id first
   * (name remains the fallback), so name-variant venues collapse onto the
   * canonical enriched row instead of duplicating.
   */
  googlePlaceId?: string;
  title: string;
  /** Scraper-tier taxonomy — what the source site calls this event. */
  rawCategory: NormalizedEventCategory;
  /**
   * Platform category slug the event will surface under. Maps to a top-level
   * `Category.slug` seeded in the DB. Mapping is the ingestor's responsibility.
   */
  platformCategory: PlatformEventCategory;
  /** ISO-8601 with timezone offset (e.g., `2026-05-01T19:00:00-04:00`). */
  startDatetime: string;
  /**
   * Honest-signal flag for the discover surface (PIP-72 / API-1410).
   *
   * `true` when the upstream source surfaced an explicit wall-clock start
   * time that flows through unchanged in `startDatetime`. `false`/`undefined`
   * when the source omitted time and a transformer defaulted (e.g. iCal
   * `DATE`, calendar listing with no time component, fake-midnight ISO).
   * Persisters write this to `Event.startTimeKnown` so the discover surface
   * can render "TBA" instead of a wrong 8 PM EDT.
   *
   * Optional on this DTO so transformers that have not been updated still
   * type-check; persisters fall back to inspecting the ISO shape when
   * omitted.
   */
  startTimeKnown?: boolean;
  /** IANA timezone (always `America/New_York` for the Charlotte MVP). */
  timezone: string;
  status: NormalizedEventStatus;
  detailUrl: string;
  /** 64-char hex SHA-256 of the raw source HTML/payload for change detection. */
  sourceHtmlHash: string;
  firstSeenAt: string;
  lastSeenAt: string;
  /** Updated only when `sourceHtmlHash` changes between fetches. */
  lastChangedAt: string;
  /** Calendar URL or API endpoint this record was scraped from. */
  scrapeSource: string;
  /** User-Agent string used for the fetch (e.g., LociBot UA). */
  scrapeUserAgent: string;

  headliner?: string;
  supporting?: string[];
  doorsDatetime?: string;
  endDatetime?: string;
  ticketUrl?: string;
  imageUrl?: string;
  /**
   * PIP-235 — other renditions of the SAME cover image the source published,
   * keyed by size band (`{thumbnail, medium, large}`), so the web surface can
   * fetch a slot-appropriate variant rather than scaling `imageUrl` everywhere.
   *
   * Only real published URLs land here — never a URL derived by rewriting a
   * size token or appending a resize parameter (Eventbrite CDN URLs are
   * HMAC-signed and 403 on an appended `w=`). A size band the source did not
   * publish is an absent key, not a substitute. `undefined` leaves
   * `Event.imageUrls` NULL.
   */
  imageUrls?: ImageUrls;
  /**
   * Lowest published ticket price in display currency (e.g. dollars, not cents).
   * `undefined` when the source did not surface a price at all — paired with
   * `priceKnown=false` to signal "unknown" rather than "free".
   */
  minPrice?: number;
  /**
   * Highest published ticket price in display currency. Equals `minPrice` for
   * single-tier events. `undefined` when the source did not surface a price.
   */
  maxPrice?: number;
  /**
   * Honest-signal flag for the discover surface (PIP-73 / API-1410).
   *
   * `true` when the source explicitly provided a price (paid event with a
   * range, or a flag-confirmed free event). `false` when the source omitted
   * price entirely — distinguishes "we know this is free" from "we don't
   * know what this costs and shouldn't lie about it".
   *
   * Optional on this DTO so existing transformers that haven't been updated
   * yet still type-check; the persister defaults to `false` when omitted,
   * matching the DB column's default. Transformers that DO know the answer
   * MUST set this explicitly so the discover surface can route paid /
   * truly-free / unknown rows into the correct UI states.
   */
  priceKnown?: boolean;
  /**
   * API-1660 — distinct published tier count. Ticketmaster fills this from
   * `priceRanges.length`. Eventbrite Apify currently exposes no tier
   * cardinality — leave `undefined` (persister writes NULL). Never invent
   * from a min/max spread. Persisted to `Event.priceTierCount`.
   */
  priceTierCount?: number;
  /**
   * API-1660 — fee disclosure matching the discover wire `plusFees` contract
   * (`true` = plus fees, `false` = included/none). Eventbrite Apify currently
   * exposes no fee signal — leave `undefined`. Persisted to `Event.plusFees`.
   */
  plusFees?: boolean;
  /**
   * PIP-161 — explicit free-entry flag (decision D6). `true` only when the
   * source declared free entry (Eventbrite actor `isFree:true`, a FREE
   * title/summary marker, or a confirmed $0/$0 range). `undefined` = unknown —
   * NEVER inferred from a missing price. Persisted to `Event.isFree`.
   */
  isFree?: boolean;
  /**
   * PIP-161 — explicit RSVP-only flag (decision D6). `true` only on an explicit
   * RSVP-without-ticketing signal (e.g. an "RSVP" marker with no ticket tier);
   * `undefined`/`false` otherwise (never guessed). Persisted to
   * `Event.rsvpOnly`.
   */
  rsvpOnly?: boolean;
  ageRestriction?: string;
  /**
   * FRE-1288 — approximate session length in minutes, derived from a trusted
   * start/end pair (or an explicit source duration when one exists). Persisted
   * to `Event.runTimeMinutes`. `undefined` when the source does not prove a
   * single-session span — never estimated from doors or genre defaults.
   */
  runTimeMinutes?: number;
  tags?: string[];
  /**
   * Raw upstream source category label (lowercased), e.g. "health & wellness".
   * Persisted to `Event.sourceCategory` so the public read filter can hide
   * non-allowlisted Eventbrite events. Only the Eventbrite Apify transformer
   * sets it today; other sources leave it undefined.
   */
  sourceCategory?: string;
  /**
   * PIP-234 — the source's own classification, persisted to
   * `Event.sourceSegment` / `sourceGenre` / `sourceSubGenre` as a finer axis
   * UNDERNEATH the six LOCI categories (never a replacement for them).
   *
   * Honest-data rule: set ONLY from a label the source actually published.
   * Never inferred from a title, venue, or artist — `undefined` leaves the
   * column NULL, which is the correct value for a source with no taxonomy.
   *
   * Per-lane coverage today:
   *   - Ticketmaster fills all three (`classifications[].segment/genre/subGenre`).
   *   - The Eventbrite Apify lane fills `sourceSegment` only, from the actor's
   *     `category`. Eventbrite publishes no genre; its `format` is a format
   *     axis ("Festival", "Concert or Performance") and is deliberately NOT
   *     mapped to a genre.
   *   - UNC-Charlotte / venue-site lanes leave all three undefined — they
   *     derive a LOCI category from title/description and publish no taxonomy.
   */
  sourceSegment?: string;
  sourceGenre?: string;
  sourceSubGenre?: string;
  /** Untransformed source payload — keep for debugging and later re-normalization. */
  rawPayload?: Record<string, unknown>;
}
